import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useProcesses } from "../App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gear, Plus, X, DownloadSimple } from "@phosphor-icons/react";
import { useMonth } from "../contexts/MonthContext";
import { toast } from "sonner";
import { exportWithHeader } from "../utils/excelExport";

// Pretty-print a number with the Indian thousands grouping; "-" for zero.
function fmt(v) {
  if (v == null || v === 0) return "-";
  return typeof v === "number"
    ? v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : "-";
}
function fmtShort(v) {
  if (v == null || v === 0) return "-";
  if (v >= 1e7) return (v / 1e7).toFixed(2) + " Cr";
  if (v >= 1e5) return (v / 1e5).toFixed(2) + " L";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + " K";
  return v.toFixed(0);
}

const LS_KEY = "dashboard_insights_v1";
// Daily Review Sheet stores its date window under this key — we re-read it
// on every fetch and re-render so changes there flow through to Insights
// automatically (no manual sync needed).
const DR_PREFS_KEY = "daily_review_prefs_v1";

// Fixed sections (always shown unless removed). Picked process codes are
// stored separately so the user can add/remove them per their needs.
const DEFAULT_SECTIONS = ["schedule_qty", "rm_qty", "rm_value", "machining"];

const ALL_SECTIONS = {
  schedule_qty: { label: "Schedule (Value)", subtitle: "₹ at sales rate", colorA: "#1D2951", colorB: "#3B82F6", unit: "₹", showValue: true },
  rm_qty:       { label: "RM to be procured", subtitle: "Kg — required vs available (RM Stock + WIP)", colorA: "#0F766E", colorB: "#14B8A6", unit: "Kg",  showValue: false },
  rm_value:     { label: "Raw Material (Value)", subtitle: "₹ at weighted avg purchase rate", colorA: "#B45309", colorB: "#F59E0B", unit: "₹", showValue: true },
  machining:    { label: "Balance Machining Quantity", subtitle: "Schedule − (Dispatched + RFD + S/F)", colorA: "#7C3AED", colorB: "#A855F7", unit: "Nos", showValue: false },
};

// Color palette for additional process blocks (cycled).
const PROC_PALETTE = [
  { colorA: "#0369A1", colorB: "#0EA5E9" }, // sky
  { colorA: "#9333EA", colorB: "#C084FC" }, // purple
  { colorA: "#DB2777", colorB: "#F472B6" }, // pink
  { colorA: "#15803D", colorB: "#22C55E" }, // green
  { colorA: "#C2410C", colorB: "#FB923C" }, // orange
];

export default function InsightsPanel() {
  // Persisted user prefs
  const _saved = (() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
    catch { return {}; }
  })();
  const [enabledSections, setEnabledSections] = useState(_saved.sections || DEFAULT_SECTIONS);
  // Each picked process = { code, kind: "qty" | "value" | "both" }
  const [pickedProcesses, setPickedProcesses] = useState(_saved.processes || []);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillTile, setDrillTile] = useState(null);
  // ── Daily mode ───────────────────────────────────────────────────────
  // When ON we hide the Schedule tile and show "Dispatch" + per-operation
  // tiles in a "Day vs Cumulative" layout. Default date = yesterday; user
  // can change via date picker. Cumulative window = DRS date_from → date.
  const _yesterday = () => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };
  const [dailyMode, setDailyMode] = useState(false);
  const [dailyDate, setDailyDate] = useState(_yesterday);
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  // Period mode toggle — "all" (default, no month filter) | "month" (uses
  // the app-wide month/year picker). Daily is a separate mode below.
  const [periodMode, setPeriodMode] = useState("all");
  // Mirror of the Daily Review Sheet's date window. We re-read localStorage
  // on focus / storage events so the user editing dates over there flows
  // through here instantly.
  const _readDR = () => {
    try {
      const v = JSON.parse(localStorage.getItem(DR_PREFS_KEY) || "{}");
      return { date_from: v.date_from || "", date_to: v.date_to || "" };
    } catch { return { date_from: "", date_to: "" }; }
  };
  const [drDates, setDrDates] = useState(_readDR);

  const { processes: allProcesses } = useProcesses();
  const { period } = useMonth();

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        sections: enabledSections,
        processes: pickedProcesses,
      }));
    } catch { /* quota — ignore */ }
  }, [enabledSections, pickedProcesses]);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const codes = pickedProcesses.map(p => p.code).join(",");
      const params = {};
      if (codes) params.processes = codes;
      // Carry the Daily Review Sheet's date window through to Insights so
      // both views stay in sync without any extra user action.
      if (drDates.date_from) params.date_from = drDates.date_from;
      if (drDates.date_to) params.date_to = drDates.date_to;
      // periodMode === "all" → do NOT let the MonthContext interceptor
      // attach month/year. Backend `_in_period` returns True for every
      // row when neither is set → all-time totals.
      const cfg = periodMode === "all" ? { params, skipPeriod: true } : { params };
      const res = await axios.get(`${API}/dashboard/insights`, cfg);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load insights");
    } finally { setLoading(false); }
  }, [pickedProcesses, drDates.date_from, drDates.date_to, periodMode]);

  useEffect(() => { fetchInsights(); }, [fetchInsights, period.month, period.year]);

  // Daily-mode fetch. Runs when the user flips on Daily or picks a new date.
  const fetchDaily = useCallback(async () => {
    if (!dailyMode) return;
    setDailyLoading(true);
    try {
      const params = { date: dailyDate };
      if (drDates.date_from) params.date_from = drDates.date_from;
      const codes = pickedProcesses.map(p => p.code).join(",");
      if (codes) params.processes = codes;
      const res = await axios.get(`${API}/dashboard/daily`, { params });
      setDailyData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load daily insights");
    } finally { setDailyLoading(false); }
  }, [dailyMode, dailyDate, drDates.date_from, pickedProcesses]);

  useEffect(() => { fetchDaily(); }, [fetchDaily]);

  // Pick up live changes to Daily Review's date window:
  //  • `storage` event fires when a DIFFERENT tab/window writes localStorage
  //  • `focus` re-reads on tab return (covers same-tab navigations to DR)
  useEffect(() => {
    const sync = () => setDrDates(_readDR());
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  // ── Section helpers ──────────────────────────────────────────────────────
  const toggleSection = (key) => {
    setEnabledSections(curr =>
      curr.includes(key) ? curr.filter(s => s !== key) : [...curr, key]
    );
  };

  const addProcess = (code, kind = "both") => {
    if (!code || pickedProcesses.find(p => p.code === code)) return;
    setPickedProcesses(p => [...p, { code, kind }]);
  };
  const removeProcess = (code) => {
    setPickedProcesses(p => p.filter(x => x.code !== code));
  };
  const setProcessKind = (code, kind) => {
    setPickedProcesses(p => p.map(x => x.code === code ? { ...x, kind } : x));
  };

  const orderedSections = useMemo(() =>
    DEFAULT_SECTIONS.filter(s => enabledSections.includes(s))
  , [enabledSections]);

  if (loading && !data) {
    return <div className="text-sm text-[#64748B] p-4">Loading insights…</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="insights-panel">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg sm:text-xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>
            Insights
          </h2>
          {(drDates.date_from || drDates.date_to) && !dailyMode && (
            <span
              className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E40AF] border border-[#BFDBFE]"
              title="Window comes from the Daily Review Sheet date filter. Change it there and it syncs back here automatically."
              data-testid="insights-window-chip"
            >
              {drDates.date_from || "…"} → {drDates.date_to || "…"}
            </span>
          )}
          {dailyMode && dailyData && (
            <span
              className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]"
              data-testid="daily-window-chip"
              title="Cumulative window — from Daily Review Sheet date_from to the selected day."
            >
              Cumulative: {dailyData.cumulative_from} → {dailyData.cumulative_to}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* All / Month / Daily period toggle */}
          <div className="inline-flex rounded-md border border-[#E2E8F0] overflow-hidden" data-testid="insights-period-toggle">
            <button
              onClick={() => { setDailyMode(false); setPeriodMode("all"); }}
              className={`text-xs h-9 px-3 font-semibold transition-colors ${!dailyMode && periodMode === "all"
                ? "bg-[#1D2951] text-white"
                : "bg-white text-[#1D2951] hover:bg-[#F1F5F9]"}`}
              data-testid="insights-mode-all"
              title="Show totals across every uploaded PO / transaction (no month filter)."
            >All</button>
            <button
              onClick={() => { setDailyMode(false); setPeriodMode("month"); }}
              className={`text-xs h-9 px-3 font-semibold transition-colors border-l border-[#E2E8F0] ${!dailyMode && periodMode === "month"
                ? "bg-[#1D2951] text-white"
                : "bg-white text-[#1D2951] hover:bg-[#F1F5F9]"}`}
              data-testid="insights-mode-month"
              title="Show only rows / transactions in the currently-selected month."
            >Month</button>
            <button
              onClick={() => setDailyMode(true)}
              className={`text-xs h-9 px-3 font-semibold transition-colors border-l border-[#E2E8F0] ${dailyMode
                ? "bg-[#B45309] text-white"
                : "bg-white text-[#B45309] hover:bg-[#FEF3C7]"}`}
              data-testid="insights-mode-daily"
              title="Day-slice view with cumulative window."
            >Daily</button>
          </div>
          {dailyMode && (
            <input
              type="date"
              value={dailyDate}
              onChange={e => setDailyDate(e.target.value)}
              className="h-9 px-2 text-xs border border-[#FCD34D] rounded-md bg-[#FFFBEB] font-mono text-[#92400E] focus:outline-none focus:ring-2 focus:ring-[#FCD34D]"
              data-testid="insights-daily-date"
              title="Pick the date you want a snapshot of — defaults to yesterday."
            />
          )}
          <Button
            onClick={() => setSettingsOpen(v => !v)}
            variant="outline"
            className="text-xs h-9 px-3 border-[#E2E8F0] hover:bg-[#F1F5F9]"
            data-testid="insights-settings-btn"
          >
            <Gear size={14} className="mr-1.5" />
            {settingsOpen ? "Close Settings" : "Customize"}
          </Button>
        </div>
      </div>

      {/* Settings drawer */}
      {settingsOpen && (
        <Card className="border border-[#E2E8F0] p-4 bg-[#F8FAFC]" data-testid="insights-settings">
          <h3 className="text-xs font-bold text-[#475569] uppercase tracking-wide mb-2">Fixed Sections</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(ALL_SECTIONS).map(([key, cfg]) => {
              const on = enabledSections.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleSection(key)}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-colors ${on
                    ? "bg-[#1D2951] text-white border-[#1D2951]"
                    : "bg-white text-[#475569] border-[#CBD5E1] hover:border-[#1D2951]"}`}
                  data-testid={`insights-toggle-${key}`}
                >
                  {on ? "✓ " : "+ "}{cfg.label}
                </button>
              );
            })}
          </div>
          <h3 className="text-xs font-bold text-[#475569] uppercase tracking-wide mb-2">Process Sections</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {pickedProcesses.length === 0 && (
              <span className="text-xs text-[#94A3B8] italic">No process sections — add one below.</span>
            )}
            {pickedProcesses.map(p => {
              const proc = allProcesses.find(pr => pr.code === p.code);
              return (
                <div key={p.code} className="inline-flex items-center gap-1 bg-white border border-[#CBD5E1] rounded-full pl-3 pr-1 py-0.5">
                  <span className="text-xs font-semibold text-[#1D2951]">{proc?.name || p.code}</span>
                  <select
                    value={p.kind}
                    onChange={e => setProcessKind(p.code, e.target.value)}
                    className="text-[10px] bg-[#F1F5F9] rounded px-1 py-0.5 border border-transparent focus:border-[#3B82F6] outline-none"
                    data-testid={`proc-kind-${p.code}`}
                  >
                    <option value="qty">Quantity only</option>
                    <option value="value">Value only</option>
                    <option value="both">Quantity + Value</option>
                  </select>
                  <button onClick={() => removeProcess(p.code)} className="text-[#94A3B8] hover:text-[#DC2626] h-6 w-6 inline-flex items-center justify-center" data-testid={`proc-remove-${p.code}`}>
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-[#3B82F6] font-bold inline-flex items-center gap-1"><Plus size={12} /> Add a process</summary>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(allProcesses || [])
                .filter(p => !pickedProcesses.find(x => x.code === p.code))
                .filter(p => p.code !== "machining_p1" && p.code !== "machining_p2")
                .map(p => (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => addProcess(p.code, "both")}
                    className="text-[11px] px-2 py-1 rounded border border-[#CBD5E1] bg-white hover:bg-[#EFF6FF] hover:border-[#3B82F6]"
                    data-testid={`proc-add-${p.code}`}
                  >
                    + {p.name}
                  </button>
                ))}
            </div>
          </details>
        </Card>
      )}

      {/* Section tiles */}
      {dailyMode ? (
        /* ── DAILY MODE: Dispatch + RM + per-operation tiles, each showing
              "Day vs Cumulative" instead of Planned/Done/Pending. */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dailyLoading && !dailyData && (
            <div className="text-sm text-[#64748B] p-4 col-span-2">Loading daily snapshot…</div>
          )}
          {dailyData && (
            <>
              {/* Dispatch tile (replaces Schedule in daily mode) */}
              <DailyTile
                title="Dispatch (Value)"
                subtitle="₹ at sales rate"
                colorA="#1D2951" colorB="#3B82F6"
                day={dailyData.dispatch.day_value}
                cumulative={dailyData.dispatch.cumulative_value}
                dateLabel={dailyData.date}
                onClick={() => setDrillTile({
                  label: `Dispatch (${dailyData.date})`,
                  isDaily: true,
                  block: { breakdown: dailyData.dispatch.breakdown || [] },
                })}
                dataTestId="daily-dispatch-tile"
              />
              {/* RM (Purchased) tile */}
              <DailyTile
                title="Raw Material (Purchased)"
                subtitle="₹ at actual purchase rate"
                colorA="#B45309" colorB="#F59E0B"
                day={dailyData.rm.day_value}
                cumulative={dailyData.rm.cumulative_value}
                dateLabel={dailyData.date}
                doneLabel="Purchased"
                dataTestId="daily-rm-tile"
              />
              {/* Per-operation process tiles: Part rate + Operation rate */}
              {(dailyData.process_blocks || []).map((pb, i) => {
                const palette = PROC_PALETTE[i % PROC_PALETTE.length];
                return (
                  <React.Fragment key={pb.operation}>
                    <DailyTile
                      title={`${pb.label} (Value · Part Rate)`}
                      subtitle="₹ at full part rate"
                      colorA={palette.colorA} colorB={palette.colorB}
                      day={pb.day_value}
                      cumulative={pb.cumulative_value}
                      dateLabel={dailyData.date}
                      onClick={() => setDrillTile({
                        label: `${pb.label} – Part Rate (${dailyData.date})`,
                        isDaily: true,
                        block: { breakdown: pb.breakdown || [] },
                      })}
                      dataTestId={`daily-proc-${pb.operation}-part`}
                    />
                    <DailyTile
                      title={`${pb.label} (Value · Operation Rate)`}
                      subtitle="₹ at operation rate"
                      colorA={palette.colorB} colorB={palette.colorA}
                      day={pb.day_op_value}
                      cumulative={pb.cumulative_op_value}
                      dateLabel={dailyData.date}
                      onClick={() => setDrillTile({
                        label: `${pb.label} – Operation Rate (${dailyData.date})`,
                        isDaily: true,
                        isOpRate: true,
                        block: { breakdown: pb.breakdown || [] },
                      })}
                      dataTestId={`daily-proc-${pb.operation}-op`}
                    />
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {orderedSections.map(key => {
          const cfg = ALL_SECTIONS[key];
          const block = key === "schedule_qty" ? data.schedule_qty
                       : key === "rm_qty" ? data.rm_qty
                       : key === "rm_value" ? data.rm_value
                       : key === "machining" ? {
                           // New: Balance Machining Quantity tile — shows the
                           // qty pending to be machined, computed as
                           // schedule − (dispatched + RFD + S/F).
                           planned: data.machining?.planned_qty,
                           done: data.machining?.done_qty,
                           pending: data.machining?.balance_machining_qty ?? 0,
                           breakdown: data.machining?.balance_machining_breakdown || [],
                         } : null;
          if (!block) return null;
          const isValue = cfg.showValue;
          return (
            <React.Fragment key={key}>
              <SectionTile
                cfg={cfg}
                block={block}
                valueLabel={isValue ? "₹" : cfg.unit}
                isValue={isValue}
                isRmQtyTile={key === "rm_qty"}
                dataTestId={`insights-section-${key}`}
                onClick={() => setDrillTile({ label: cfg.label, block, isValue, isRmQty: key === "rm_qty" })}
              />
              {/* For machining (the only op-driven fixed section), also show
                  the "by Operation Rate" value tile. Schedule + RM tiles
                  intentionally skip this — Operation Rate has no meaning
                  for them. */}
              {key === "machining" && data.machining && (
                <SectionTile
                  cfg={{ ...cfg, label: "Machining (Value · Operation Rate)", subtitle: "₹ at operation rate", colorA: "#9333EA", colorB: "#C084FC" }}
                  block={{
                    planned: data.machining?.planned_op_value,
                    done: data.machining?.done_op_value,
                    pending: data.machining?.pending_op_value,
                    breakdown: data.machining?.breakdown || [],
                  }}
                  valueLabel="₹"
                  isValue={true}
                  isRmQtyTile={false}
                  isOpRate={true}
                  dataTestId="insights-section-machining-op"
                  onClick={() => setDrillTile({
                    label: "Machining – Operation Rate",
                    block: {
                      planned: data.machining?.planned_op_value,
                      done: data.machining?.done_op_value,
                      pending: data.machining?.pending_op_value,
                      breakdown: data.machining?.breakdown || [],
                    },
                    isValue: true, isOpRate: true,
                  })}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* User-picked process blocks */}
        {pickedProcesses.map((p, i) => {
          const proc = data.process_blocks.find(b => (b.operations || [])[0] === p.code);
          if (!proc) return null;
          const palette = PROC_PALETTE[i % PROC_PALETTE.length];
          return (
            <ProcessTilePair
              key={p.code}
              kind={p.kind}
              proc={proc}
              palette={palette}
              onOpen={(label, block, isValue, isOpRate) => setDrillTile({ label, block, isValue, isOpRate, isRmQty: false })}
            />
          );
        })}
      </div>
      )}

      {/* Drill-down modal */}
      {drillTile && (
        <DrillModal tile={drillTile} onClose={() => setDrillTile(null)} />
      )}
    </div>
  );
}

// ─── Single Section Tile ────────────────────────────────────────────────────
// Three big numbers (planned / done / pending) with a horizontal progress
// bar. Visually distinctive but uses existing colour tokens.
function SectionTile({ cfg, block, valueLabel, isValue, dataTestId, onClick, isRmQtyTile }) {
  const planned = block.planned || 0;
  const doneKey = block.done != null ? "done" : "purchased";
  const done = block[doneKey] || 0;
  const pending = block.pending || 0;
  const pct = planned > 0 ? Math.min(100, (done / planned) * 100) : 0;
  const f = isValue ? fmtShort : fmt;
  return (
    <Card
      className="border border-[#E2E8F0] overflow-hidden cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-transform"
      data-testid={dataTestId}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onClick) onClick(); }}
    >
      {/* Header band */}
      <div className="px-4 py-2.5" style={{ background: `linear-gradient(90deg, ${cfg.colorA}, ${cfg.colorB})` }}>
        <p className="text-white text-[10px] uppercase tracking-widest font-bold opacity-90">{cfg.subtitle}</p>
        <p className="text-white text-base sm:text-lg font-black" style={{ fontFamily: 'Chivo' }}>{cfg.label}</p>
      </div>
      {/* Three numbers */}
      <div className="grid grid-cols-3 divide-x divide-[#E2E8F0]">
        <div className="p-3 sm:p-4 text-center">
          <p className="text-[10px] text-[#64748B] uppercase font-bold">Planned</p>
          <p className="text-lg sm:text-xl font-black text-[#1D2951] font-mono mt-0.5" title={String(planned)}>
            {f(planned)}{!isValue && <span className="text-[10px] font-semibold text-[#94A3B8] ml-1">{valueLabel}</span>}
          </p>
        </div>
        <div className="p-3 sm:p-4 text-center">
          <p className="text-[10px] text-[#64748B] uppercase font-bold">{isRmQtyTile ? "Available" : "Done"}</p>
          <p className="text-lg sm:text-xl font-black font-mono mt-0.5" style={{ color: cfg.colorA }} title={String(done)}>
            {f(done)}{!isValue && <span className="text-[10px] font-semibold text-[#94A3B8] ml-1">{valueLabel}</span>}
          </p>
        </div>
        <div className="p-3 sm:p-4 text-center">
          <p className="text-[10px] text-[#64748B] uppercase font-bold">Pending</p>
          <p className="text-lg sm:text-xl font-black text-[#DC2626] font-mono mt-0.5" title={String(pending)}>
            {f(pending)}{!isValue && <span className="text-[10px] font-semibold text-[#94A3B8] ml-1">{valueLabel}</span>}
          </p>
        </div>
      </div>
      {/* Progress bar */}
      <div className="px-4 pb-3 pt-1">
        <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${cfg.colorA}, ${cfg.colorB})` }} />
        </div>
        <p className="mt-1 text-[10px] text-[#64748B] font-mono text-right">
          {pct.toFixed(1)}% complete · unit: {valueLabel} · <span className="text-[#3B82F6]">click for breakdown</span>
        </p>
      </div>
    </Card>
  );
}

// Renders Qty tile, Value tile(s), or both. When kind includes a value tile
// we now render TWO of them — one priced at part rate, one at operation rate
// — so the user sees value-add by stage AND total commercial value side-by-
// side.
function ProcessTilePair({ kind, proc, palette, onOpen }) {
  const showQty = kind === "qty" || kind === "both";
  const showVal = kind === "value" || kind === "both";
  // Secondary palette for the Op-Rate tile so it visually pairs with the
  // primary palette but reads as a distinct sibling.
  const palette2 = { colorA: palette.colorB, colorB: palette.colorA };
  return (
    <>
      {showQty && (
        <SectionTile
          cfg={{ ...palette, label: proc.label, subtitle: "Quantity (Nos)", showValue: false, unit: "Nos" }}
          block={{ planned: proc.planned_qty, done: proc.done_qty, pending: proc.pending_qty, breakdown: proc.breakdown || [] }}
          valueLabel="Nos"
          isValue={false}
          dataTestId={`insights-section-${proc.operations[0]}-qty`}
          onClick={() => onOpen && onOpen(`${proc.label} (Quantity)`, { planned: proc.planned_qty, done: proc.done_qty, pending: proc.pending_qty, breakdown: proc.breakdown || [] }, false, false)}
        />
      )}
      {showVal && (
        <>
          <SectionTile
            cfg={{ ...palette, label: `${proc.label} (Value · Part Rate)`, subtitle: "₹ at full part rate", showValue: true, unit: "₹" }}
            block={{ planned: proc.planned_value, done: proc.done_value, pending: proc.pending_value, breakdown: proc.breakdown || [] }}
            valueLabel="₹"
            isValue={true}
            dataTestId={`insights-section-${proc.operations[0]}-val`}
            onClick={() => onOpen && onOpen(`${proc.label} – Part Rate`, { planned: proc.planned_value, done: proc.done_value, pending: proc.pending_value, breakdown: proc.breakdown || [] }, true, false)}
          />
          <SectionTile
            cfg={{ ...palette2, label: `${proc.label} (Value · Operation Rate)`, subtitle: "₹ at operation rate", showValue: true, unit: "₹" }}
            block={{ planned: proc.planned_op_value, done: proc.done_op_value, pending: proc.pending_op_value, breakdown: proc.breakdown || [] }}
            valueLabel="₹"
            isValue={true}
            isOpRate={true}
            dataTestId={`insights-section-${proc.operations[0]}-opval`}
            onClick={() => onOpen && onOpen(`${proc.label} – Operation Rate`, { planned: proc.planned_op_value, done: proc.done_op_value, pending: proc.pending_op_value, breakdown: proc.breakdown || [] }, true, true)}
          />
        </>
      )}
    </>
  );
}

// ─── Daily Tile ─────────────────────────────────────────────────────────────
// Two big numbers: "On {date}" and "Cumulative to date". Clicking opens
// the same drill-down modal but with daily-flavoured rows.
function DailyTile({ title, subtitle, colorA, colorB, day, cumulative, dateLabel, doneLabel = "Dispatched", onClick, dataTestId }) {
  return (
    <Card
      className={`border border-[#E2E8F0] overflow-hidden transition-transform ${onClick ? "cursor-pointer hover:shadow-lg hover:-translate-y-0.5" : ""}`}
      data-testid={dataTestId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onClick) onClick(); }}
    >
      <div className="px-4 py-2.5" style={{ background: `linear-gradient(90deg, ${colorA}, ${colorB})` }}>
        <p className="text-white text-[10px] uppercase tracking-widest font-bold opacity-90">{subtitle}</p>
        <p className="text-white text-base sm:text-lg font-black" style={{ fontFamily: 'Chivo' }}>{title}</p>
      </div>
      <div className="grid grid-cols-2 divide-x divide-[#E2E8F0]">
        <div className="p-3 sm:p-4 text-center">
          <p className="text-[10px] text-[#64748B] uppercase font-bold">On {dateLabel}</p>
          <p className="text-lg sm:text-xl font-black font-mono mt-0.5" style={{ color: colorA }} title={String(day)}>{fmtShort(day)}</p>
          <p className="text-[10px] text-[#94A3B8] mt-0.5">{doneLabel}</p>
        </div>
        <div className="p-3 sm:p-4 text-center bg-[#F8FAFC]">
          <p className="text-[10px] text-[#64748B] uppercase font-bold">Cumulative</p>
          <p className="text-lg sm:text-xl font-black text-[#1D2951] font-mono mt-0.5" title={String(cumulative)}>{fmtShort(cumulative)}</p>
          <p className="text-[10px] text-[#94A3B8] mt-0.5">Till date</p>
        </div>
      </div>
      {onClick && (
        <div className="px-4 pb-2 pt-0">
          <p className="text-[10px] text-[#3B82F6] text-right">click for breakdown</p>
        </div>
      )}
    </Card>
  );
}

// ─── Drill-Down Modal ───────────────────────────────────────────────────────
// Lists every part (or rm_code) contributing to the tile's three numbers.
// Two shapes are supported:
//   • Standard (planned/done/pending) — used by all non-daily tiles.
//   • Daily (day_qty / cumulative_qty / day_value / cumulative_value) — used
//     when the tile is clicked while in Daily mode.
function DrillModal({ tile, onClose }) {
  const rows = tile.block?.breakdown || [];
  const isRm = tile.isRmQty;
  const isDaily = tile.isDaily;
  const isOpRate = tile.isOpRate;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="insights-drill-modal"
    >
      <div
        className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 bg-[#0F172A] text-white">
          <div>
            <h3 className="font-black text-base" style={{ fontFamily: "Chivo" }}>{tile.label}</h3>
            <p className="text-[10px] text-[#94A3B8] mt-0.5">
              {rows.length} part{rows.length === 1 ? "" : "s"} contributing
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!rows.length) { toast.error("No rows to export"); return; }
                // Build a sheet-friendly row set; column shape depends on the
                // tile flavour (daily / op-rate / standard part-rate).
                const data = rows.map(r => {
                  if (isDaily) {
                    return {
                      "Customer": r.customer || "",
                      "Part No.": r.part_no || "",
                      "Part Name": r.part_name || "",
                      [isOpRate ? "Operation Rate" : "Rate"]: isOpRate ? r.op_rate : r.rate,
                      "Day Quantity": r.day_qty,
                      "Cumulative Quantity": r.cumulative_qty,
                      "Day ₹": isOpRate ? r.day_op_value : r.day_value,
                      "Cumulative ₹": isOpRate ? r.cumulative_op_value : r.cumulative_value,
                    };
                  }
                  const base = isRm
                    ? {
                        "Sr No": r.sr_no,
                        "Part No.": r.part_no || "",
                        "Part Name": r.part_name || "",
                        "Customer": r.customer || "",
                        "RM Code": r.rm_code || "",
                        "Gross Weight": r.gross_weight,
                      }
                    : {
                        "Customer": r.customer || "",
                        "Part No.": r.part_no || "",
                        "Part Name": r.part_name || "",
                      };
                  if (isRm) {
                    return {
                      ...base,
                      "Rate": r.rate,
                      "Required Quantity (Kg)": r.required_qty,
                      "Required ₹": r.required_value,
                      "RM Stock (Kg)": r.rm_stock_qty,
                      "Stock ₹": r.rm_stock_value,
                      "WIP (Kg)": r.wip_qty,
                      "WIP ₹": r.wip_value,
                      "RM Bought (Kg)": r.bought_qty,
                      "Bought ₹": r.bought_value,
                      "Balance Procurement (Kg)": r.balance_qty,
                      "Balance ₹": r.balance_value,
                    };
                  }
                  return {
                    ...base,
                    [isOpRate ? "Operation Rate" : "Rate"]: isOpRate ? r.op_rate : r.rate,
                    "Planned Quantity": r.planned_qty,
                    "Done Quantity": r.done_qty,
                    "Pending Quantity": r.pending_qty,
                    "Planned ₹": isOpRate ? r.planned_op_value : r.planned_value,
                    "Done ₹": isOpRate ? r.done_op_value : r.done_value,
                    "Pending ₹": isOpRate ? r.pending_op_value : r.pending_value,
                  };
                });
                const safe = (tile.label || "tile").replace(/[^a-zA-Z0-9-]+/g, "-").slice(0, 40);
                exportWithHeader(
                  [{ name: safe.slice(0, 31), data }],
                  `Insights-${safe}-${new Date().toISOString().split("T")[0]}`,
                  tile.label,
                );
              }}
              className="flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded bg-[#D4A843] hover:bg-[#B8902F] text-[#0F172A] transition-colors"
              data-testid="drill-export-excel"
              title="Export this breakdown to Excel"
            >
              <DownloadSimple size={14} weight="bold" /> Export Excel
            </button>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none ml-1" data-testid="drill-close">×</button>
          </div>
        </div>
        <div className="overflow-auto">
          {isDaily ? (
            <table className="w-full text-xs">
              <thead className="bg-[#F1F5F9] text-[#475569] uppercase text-[10px] sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Part No.</th>
                  <th className="px-3 py-2 text-left">Part Name</th>
                  <th className="px-3 py-2 text-right">{isOpRate ? "Operation Rate" : "Rate"}</th>
                  <th className="px-3 py-2 text-right">Day Quantity</th>
                  <th className="px-3 py-2 text-right">Cumulative Quantity</th>
                  <th className="px-3 py-2 text-right">Day ₹</th>
                  <th className="px-3 py-2 text-right">Cumulative ₹</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {rows.map((r, i) => (
                  <tr key={(r.part_id || r.rm_code) + i} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                    <td className="px-3 py-1.5 text-[#475569]">{r.customer || "—"}</td>
                    <td className="px-3 py-1.5 font-mono font-bold text-[#1D2951]">{r.part_no}</td>
                    <td className="px-3 py-1.5 text-[#475569] truncate max-w-[200px]" title={r.part_name}>{r.part_name}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#64748B]">{fmt(isOpRate ? r.op_rate : r.rate)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(r.day_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(r.cumulative_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#059669]">{fmtShort(isOpRate ? r.day_op_value : r.day_value)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold text-[#1D2951]">{fmtShort(isOpRate ? r.cumulative_op_value : r.cumulative_value)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="py-12 text-center text-[#94A3B8]">No activity in this window.</td></tr>
                )}
              </tbody>
            </table>
          ) : isRm ? (
            /* Per-part RM breakdown (user spec) — one row per part with
                Required, RM Stock, WIP, RM Bought, Balance Procurement. */
            <table className="w-full text-xs">
              <thead className="bg-[#F1F5F9] text-[#475569] uppercase text-[10px] sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-center w-10">#</th>
                  <th className="px-3 py-2 text-left">Part No.</th>
                  <th className="px-3 py-2 text-left">Part Name</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">RM Code</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right bg-[#0F172A] text-white">Required (Kg)</th>
                  <th className="px-3 py-2 text-right bg-[#0F172A] text-white">Required ₹</th>
                  <th className="px-3 py-2 text-right bg-[#0F766E] text-white">RM Stock (Kg)</th>
                  <th className="px-3 py-2 text-right bg-[#0F766E] text-white">Stock ₹</th>
                  <th className="px-3 py-2 text-right bg-[#B45309] text-white">WIP (Kg)</th>
                  <th className="px-3 py-2 text-right bg-[#B45309] text-white">WIP ₹</th>
                  <th className="px-3 py-2 text-right bg-[#4C1D95] text-white">RM Bought (Kg)</th>
                  <th className="px-3 py-2 text-right bg-[#4C1D95] text-white">Bought ₹</th>
                  <th className="px-3 py-2 text-right bg-[#7F1D1D] text-white">Balance (Kg)</th>
                  <th className="px-3 py-2 text-right bg-[#7F1D1D] text-white">Balance ₹</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {rows.map((r, i) => (
                  <tr key={(r.part_id || r.part_no) + i} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                    <td className="px-2 py-1.5 text-center font-mono text-[#64748B]">{r.sr_no}</td>
                    <td className="px-3 py-1.5 font-mono font-bold text-[#1D2951]">
                      <div>{r.part_no}</div>
                      {r.company_part_no && r.company_part_no !== r.part_no && (
                        <div className="text-[9px] font-mono text-[#1D4ED8] mt-0.5 truncate max-w-[160px]" title={r.company_part_no_description || r.company_part_no}>
                          ↳ {r.company_part_no}{r.company_part_no_description ? ` · ${r.company_part_no_description}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-[#475569] truncate max-w-[180px]" title={r.part_name}>{r.part_name || "—"}</td>
                    <td className="px-3 py-1.5 text-[#64748B] truncate max-w-[120px]" title={r.customer}>{r.customer || "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-[#475569]">{r.rm_code || "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#64748B]">₹{fmt(r.rate)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold">{fmt(r.required_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtShort(r.required_value)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#0F766E]">{fmt(r.rm_stock_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#0F766E]">{fmtShort(r.rm_stock_value)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#B45309]">{fmt(r.wip_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#B45309]">{fmtShort(r.wip_value)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#7C3AED]">{fmt(r.bought_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#7C3AED]">{fmtShort(r.bought_value)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-black text-[#DC2626]">{fmt(r.balance_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-black text-[#DC2626]">{fmtShort(r.balance_value)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={16} className="py-12 text-center text-[#94A3B8]">No parts in this window.</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-[#F1F5F9] text-[#475569] uppercase text-[10px] sticky top-0">
                <tr>
                  {isRm ? <th className="px-3 py-2 text-left">RM Code</th> : <>
                    <th className="px-3 py-2 text-left">Customer</th>
                    <th className="px-3 py-2 text-left">Part No.</th>
                    <th className="px-3 py-2 text-left">Part Name</th>
                  </>}
                  <th className="px-3 py-2 text-right">{isOpRate ? "Operation Rate" : "Rate"}</th>
                  <th className="px-3 py-2 text-right">Planned</th>
                  <th className="px-3 py-2 text-right">{isRm ? "Purchased" : "Done"}</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Planned ₹</th>
                  <th className="px-3 py-2 text-right">{isRm ? "Purchased" : "Done"} ₹</th>
                  <th className="px-3 py-2 text-right">Pending ₹</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {rows.map((r, i) => (
                  <tr key={(r.part_id || r.rm_code) + i} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                    {isRm ? <td className="px-3 py-1.5 font-mono font-bold text-[#1D2951]">{r.rm_code}</td> : <>
                      <td className="px-3 py-1.5 text-[#475569]">{r.customer || "—"}</td>
                      <td className="px-3 py-1.5 font-mono font-bold text-[#1D2951]">{r.part_no}</td>
                      <td className="px-3 py-1.5 text-[#475569] truncate max-w-[200px]" title={r.part_name}>{r.part_name}</td>
                    </>}
                    <td className="px-3 py-1.5 text-right font-mono text-[#64748B]">{fmt(isOpRate ? r.op_rate : r.rate)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmt(r.planned_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#059669]">{fmt(r.purchased_qty != null ? r.purchased_qty : r.done_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#DC2626] font-semibold">{fmt(r.pending_qty)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtShort(isOpRate ? r.planned_op_value : r.planned_value)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#059669]">{fmtShort(r.purchased_value != null ? r.purchased_value : (isOpRate ? r.done_op_value : r.done_value))}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#DC2626] font-semibold">{fmtShort(isOpRate ? r.pending_op_value : r.pending_value)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="py-12 text-center text-[#94A3B8]">No data for this tile in the chosen window.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
